/**
 * Paths: `/launch#lt=` (bootstrap), `/dial?uri=tel:...` (protocol handler -> confirm chip),
 * `/missed`, `/self-check`, `/` (phone). `launchQueue` (focus-existing) routes second launches,
 * tel: clicks and push clicks into the one running window.
 */
import { signal } from '@preact/signals';
import { telUriToNumber } from '../ui/format.js';

export type Route = { name: 'phone' } | { name: 'missed' } | { name: 'self-check' } | { name: 'dial'; number: string | null } | { name: 'launch'; lt: string | null };

export function parseRoute(pathname: string, search: string, hash: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/launch') {
    const lt = hash ? (hash.startsWith('#') ? hash.slice(1) : hash).split('&').map((p) => p.split('=')).find(([k]) => k === 'lt')?.[1] ?? null : null;
    return { name: 'launch', lt: lt ? safeDecode(lt) : null };
  }
  if (path === '/dial') {
    const uri = new URLSearchParams(search).get('uri');
    return { name: 'dial', number: uri ? telUriToNumber(uri) : null };
  }
  if (path === '/missed') return { name: 'missed' };
  if (path === '/self-check') return { name: 'self-check' };
  return { name: 'phone' };
}

function safeDecode(v: string): string {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

export class Router {
  readonly route = signal<Route>({ name: 'phone' });
  /** a tel: target waiting for the agent's confirmation */
  readonly dialRequest = signal<string | null>(null);

  constructor(private readonly win: Window = window) {}

  start(onLaunchToken: (lt: string) => void): void {
    this.apply(this.win.location.pathname, this.win.location.search, this.win.location.hash, onLaunchToken);
    this.win.addEventListener('popstate', () => this.apply(this.win.location.pathname, this.win.location.search, this.win.location.hash, onLaunchToken));
    this.win.launchQueue?.setConsumer((params) => {
      if (!params.targetURL) return;
      try {
        const u = new URL(params.targetURL);
        this.apply(u.pathname, u.search, u.hash, onLaunchToken);
      } catch {
        /* ignore */
      }
    });
  }

  private apply(pathname: string, search: string, hash: string, onLaunchToken: (lt: string) => void): void {
    const r = parseRoute(pathname, search, hash);
    if (r.name === 'launch') {
      if (r.lt) onLaunchToken(r.lt);
      // the token must not stay in the address bar / history
      this.replace('/');
      this.route.value = { name: 'phone' };
      return;
    }
    if (r.name === 'dial') {
      this.dialRequest.value = r.number;
      this.replace('/');
      this.route.value = { name: 'phone' };
      return;
    }
    this.route.value = r;
  }

  navigate(path: '/' | '/missed' | '/self-check'): void {
    try {
      this.win.history.pushState(null, '', path);
    } catch {
      /* ignore */
    }
    this.route.value = parseRoute(path, '', '');
  }

  private replace(path: string): void {
    try {
      this.win.history.replaceState(null, '', path);
    } catch {
      /* ignore */
    }
  }
}
